/**
 * "Add calendar" tabs for read-only calendars: subscribe to an internet calendar (ICS/webcal link) and choose
 * holiday calendars and the birthdays of the contacts.
 */
import { useI18n } from '@platform/i18n';
import { Button, Checkbox, ErrorState, Input, Label, Skeleton, TabsContent, TabsTrigger, toast, useAsync, useErrorText } from '@platform/ui';
import { useId, useState, type FormEvent } from 'react';
import { api } from '../api';
import { useCalendar } from './calendar-context';
import { regionName } from './events';

export function AddCalendarTriggers() {
  const { t } = useI18n();
  return (
    <>
      <TabsTrigger value="internet">{t('webmail.calendarUx.virtual.internet')}</TabsTrigger>
      <TabsTrigger value="holidays">{t('webmail.calendarUx.virtual.holidaysTab')}</TabsTrigger>
    </>
  );
}

export function AddCalendarPanels({ onDone }: { onDone: () => void }) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { changed } = useCalendar();
  const ids = { url: useId(), name: useId() };
  const [url, setUrl] = useState('');
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const options = useAsync(() => api.virtualCalendars());

  const subscribe = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    try {
      const calendar = await api.subscribeCalendar({ url: url.trim(), ...(name.trim() && { name: name.trim() }) });
      toast.success(t('webmail.calendar.add.addedToast', { name: calendar.name }));
      changed();
      onDone();
    } catch (error) {
      errorText.notify(error);
    } finally {
      setBusy(false);
    }
  };
  const setOptions = async (patch: { holidays?: string[]; birthdays?: boolean }) => {
    try {
      await api.setVirtualOptions(patch as Parameters<typeof api.setVirtualOptions>[0]);
      options.reload();
      changed();
    } catch (error) {
      errorText.notify(error);
    }
  };

  return (
    <>
      <TabsContent value="internet" className="pt-3">
        <form className="flex flex-col gap-3" onSubmit={(e) => void subscribe(e)}>
          <p className="text-sm text-muted-foreground">{t('webmail.calendarUx.virtual.internetDescription')}</p>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.url}>{t('webmail.calendarUx.virtual.url')}</Label>
            <Input id={ids.url} type="url" required value={url} placeholder={t('webmail.calendarUx.virtual.urlPlaceholder')} onChange={(e) => setUrl(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.name}>{t('webmail.calendarUx.virtual.name')}</Label>
            <Input id={ids.name} value={name} maxLength={255} onChange={(e) => setName(e.target.value)} />
          </div>
          <div className="flex justify-end">
            <Button type="submit" disabled={busy || url.trim() === ''}>
              {t('webmail.calendarUx.virtual.subscribe')}
            </Button>
          </div>
        </form>
      </TabsContent>
      <TabsContent value="holidays" className="flex flex-col gap-3 pt-3">
        {options.error ? (
          <ErrorState onRetry={options.reload} />
        ) : !options.data ? (
          <Skeleton className="h-24 w-full" />
        ) : (
          <>
            <fieldset className="flex flex-col gap-2">
              <legend className="mb-1 text-sm font-medium">{t('webmail.calendarUx.virtual.holidaysTab')}</legend>
              {options.data.countries.map((cc) => (
                <label key={cc} className="flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={options.data?.holidays.includes(cc) ?? false}
                    onCheckedChange={(on) => void setOptions({ holidays: on === true ? [...(options.data?.holidays ?? []), cc] : (options.data?.holidays ?? []).filter((x) => x !== cc) })}
                  />
                  {regionName(cc)}
                </label>
              ))}
            </fieldset>
            <label className="flex items-center gap-2 text-sm">
              <Checkbox checked={options.data.birthdays} onCheckedChange={(on) => void setOptions({ birthdays: on === true })} />
              {t('webmail.calendarUx.virtual.birthdaysOption')}
            </label>
          </>
        )}
      </TabsContent>
    </>
  );
}
